import type { Row } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Plus, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import {
  DataTableColumnHeader,
  DataTablePagination,
  OrganizationUsersTableToolbar,
} from '@/components/data-table'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  OrganizationUsersActionsCell,
  OrganizationUsersEmailCell,
} from '@/features/organization-users/components/organization-users-table-cells'
import type { OrganizationUser } from '@/features/organization-users/types/organization-user'

const columnHelper = createColumnHelper<OrganizationUser>()

function organizationUsersGlobalFilterFn(
  row: Row<OrganizationUser>,
  _columnId: string,
  filterValue: unknown
): boolean {
  const query = String(filterValue ?? '')
    .trim()
    .toLowerCase()
  if (!query) {
    return true
  }
  const user = row.original
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.toLowerCase()
  const email = String(user.email ?? '').toLowerCase()
  const roles = user.permissions
    .map((permission) => String(permission.roleSlug ?? '').toLowerCase())
    .join(' ')
  return (
    name.includes(query) ||
    email.includes(query) ||
    roles.includes(query)
  )
}

type OrganizationUsersTableProps = {
  data: OrganizationUser[]
  organizationId: string
  selfUserId: string
  onInvite: () => void
}

export function OrganizationUsersTable({
  data,
  organizationId,
  selfUserId,
  onInvite,
}: OrganizationUsersTableProps) {
  const { t } = useTranslation('organizationUsers')
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'name', desc: false },
  ])
  const [globalFilter, setGlobalFilter] = useState('')

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(),
        {
          id: 'name',
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title={t('sections.usersList.table.name')}
            />
          ),
          cell: ({ row }) => (
            <span>
              {row.original.firstName} {row.original.lastName}
            </span>
          ),
        }
      ),
      columnHelper.accessor((row) => row.email, {
        id: 'email',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('sections.usersList.table.email')}
          />
        ),
        cell: ({ row }) => <OrganizationUsersEmailCell user={row.original} />,
      }),
      columnHelper.accessor(
        (row) =>
          row.permissions.map((permission) => permission.roleSlug).join(','),
        {
          id: 'roles',
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title={t('sections.usersList.table.role')}
            />
          ),
          cell: ({ row }) =>
            row.original.permissions
              .map((permission) => t(`roles.${permission.roleSlug}`))
              .join(', '),
        }
      ),
      columnHelper.display({
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        header: () => (
          <span className="sr-only">{t('sections.usersList.table.actions')}</span>
        ),
        cell: ({ row }) => (
          <OrganizationUsersActionsCell
            organizationId={organizationId}
            selfUserId={selfUserId}
            user={row.original}
          />
        ),
      }),
    ],
    [organizationId, selfUserId, t]
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: organizationUsersGlobalFilterFn,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: { pageSize: 20, pageIndex: 0 },
    },
  })

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16">
        <Users className="size-10 text-muted-foreground/50" />
        <div className="space-y-1 text-center">
          <h3 className="text-lg font-semibold">
            {t('sections.usersList.emptyState.title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('sections.usersList.emptyState.description')}
          </p>
        </div>
        <Button type="button" onClick={onInvite}>
          <Plus className="size-4" />
          {t('actions.inviteUser')}
        </Button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'max-sm:has-[div[role=toolbar]]:mb-16',
        'flex flex-col gap-4'
      )}
    >
      <div role="toolbar" className="flex flex-col gap-2">
        <OrganizationUsersTableToolbar
          table={table}
          searchPlaceholder={t('sections.usersList.searchPlaceholder')}
          resetLabel={t('sections.usersList.resetFilters')}
        />
      </div>
      <div className="overflow-hidden rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="group/row">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      'bg-background group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted',
                      header.column.id === 'name' &&
                        'w-[20%] min-w-0 max-w-[20%]',
                      header.column.id === 'email' &&
                        'w-[32%] min-w-0 max-w-[32%]',
                      header.column.id === 'roles' &&
                        'w-[22%] min-w-0 max-w-[22%]',
                      header.column.id === 'actions' &&
                        'w-[26%] min-w-0 max-w-[26%]'
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        cell.column.id === 'name' &&
                          'w-[20%] min-w-0 max-w-[20%] whitespace-normal',
                        cell.column.id === 'email' &&
                          'w-[32%] min-w-0 max-w-[32%] whitespace-normal',
                        cell.column.id === 'roles' &&
                          'w-[22%] min-w-0 max-w-[22%] whitespace-normal',
                        cell.column.id === 'actions' &&
                          'w-[26%] min-w-0 max-w-[26%] whitespace-normal text-end'
                      )}
                      onClick={(event) => {
                        if (cell.column.id === 'actions') {
                          event.stopPropagation()
                        }
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {t('sections.usersList.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination
        table={table}
        className="mt-auto"
        rowsPerPageLabel={t('sections.usersList.rowsPerPage')}
        pageLabel={(current, total) =>
          t('sections.usersList.pageStatus', { current, total })
        }
      />
    </div>
  )
}
